<template>
  <div v-click-outside="close" class="c-dropdown" :class="rootClasses">
    <CButton ref="toggle" :theme :disabled :type @click.stop.prevent="doToggle" class="c-dropdown-toggle" :fontWeight="props.fontWeight">
      <slot name="toggle">
        <slot name="icon"><CIcon v-if="icon" :name="icon"/></slot>
        <span v-if="title" class="c-dropdown-title">{{ title }}</span>
        <span v-else-if="placeholder" class="c-dropdown-placeholder">{{ placeholder }}</span>
        <CDirectionIcon :direction="toggleDirection"/>
      </slot>
    </CButton>

    <CMenu v-if="activeInternal" ref="panel" theme="shadow" @click.stop.prevent="close" class="c-dropdown-panel" :style="floatingStyles">
      <slot/>
    </CMenu>
  </div>
</template>

<script setup lang="ts">
  import { offset, shift, useFloating, autoPlacement, autoUpdate, hide } from '@floating-ui/vue';
  import { computed, nextTick, ref, watchEffect } from 'vue';
  import { present } from '../../utils/typescript';
  import CDirectionIcon from '../CDirectionIcon.vue';
  import CIcon from '../CIcon.vue';
  import CMenu from '../CMenu.vue';
  import CButton, { Theme, Type } from '../buttons/CButton.vue';

  const props = defineProps<{
    title?: string;
    icon?: string;
    theme?: Theme;
    up?: boolean;
    active?: boolean;
    keepActive?: boolean;
    type?: Type;
    disabled?: boolean;
    canOpenSideways?: boolean;
    placeholder?: string;
    fontWeight?: 'normal' | 'bold';
  }>();

  const emit = defineEmits<{
    (e: 'update:active', value: boolean): void,
  }>();

  const toggle = ref(null);
  const panel = ref(null);
  const offsetAmount = 5;

  const { floatingStyles, middlewareData } = useFloating(toggle, panel, {
    middleware: [offset(offsetAmount), autoPlacement({
      allowedPlacements: ['top-start', 'bottom-start', ...(props.canOpenSideways ? ['right-start', 'left-start'] : [])],
      crossAxis: true,
    }), shift(), hide()],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  function addListeners() {
    window.addEventListener('resize', setMinimumPanelWidth, true);
  }

  function removeListeners() {
    window.removeEventListener('resize', setMinimumPanelWidth);
  }

  function setMinimumPanelWidth() {
    if (!panel.value) return;
    const rect = present(toggle.value).$el.getBoundingClientRect();
    const style = present(panel.value).$el.style;
    style.minWidth = `${rect.width}px`;
  }

  // we use an internal active flag because the active prop is not required and the component needs to be able to function without it being bound
  const activeInternal = ref(false);

  const rootClasses = computed(() => ({
    'c-dropdown-active': activeInternal.value,
  }));

  const toggleDirection = computed<'up' | 'down'>(() => {
    if (props.up) {
      return activeInternal.value ? 'down' : 'up';
    } else {
      return activeInternal.value ? 'up' : 'down';
    }
  });

  watchEffect(() => activeInternal.value = props.active ?? false);

  watchEffect(() => middlewareData.value.hide?.referenceHidden && close());

  watchEffect(() => {
    if (activeInternal.value) {
      nextTick(setMinimumPanelWidth);
      nextTick(addListeners);
    } else {
      removeListeners();
    }
    emit('update:active', activeInternal.value);
  });

  function doToggle() {
    if (activeInternal.value) {
      close();
    } else {
      activeInternal.value = true;
    }
  }

  function close() {
    if (!props.keepActive) activeInternal.value = false;
  }
</script>

<style lang="scss">
  .c-dropdown {
    &-toggle {
      justify-content: space-between;
      padding: .5rem;
      width: 100%;
    }

    &-title {
      flex: 1;
      text-align: left;
    }

    .c-direction-icon {
      &:not(:first-child) {
        margin-left: 1rem;
      }
    }

    &-panel {
      overflow: auto;
      max-height: calc(50vh - 4rem);
      z-index: $dropdown-layer;
    }

    .c-field {
      margin-top: 0;
    }

    .c-field-checklist, .c-field-checkbox {
      .c-field-control {
        border: none;
      }
    }
  }

  .c-dropdown-placeholder {
    color: $input-placeholder;
    font-weight: $font-weight-normal;
  }
</style>
